import { useMutation } from "@tanstack/react-query"
import { authClient } from "@/shared/api/auth-client"
import { AUTH_ENDPOINTS, AUTH_STORAGE_KEYS } from "@/shared/config/auth"
import type { User } from "@/shared/lib/types"

/** Shape of the response returned by the auth endpoints, including the JWT and optional user profile. */
interface AuthResponse {
	accessToken: string
	expiresIn: string
	user?: User
}

/** Credentials accepted by the email/password login endpoint. */
export interface EmailCredentials {
	email: string
	password: string
}

/**
 * Вход по email и паролю через отдельную ручку бэкофиса: аккаунт без роли
 * MODERATOR получит 403 сразу на входе, а не пустой интерфейс после него.
 */
export const authenticateEmail = async ({
	email,
	password,
}: EmailCredentials): Promise<AuthResponse> => {
	const { data } = await authClient.post<AuthResponse>(AUTH_ENDPOINTS.LOGIN, {
		email,
		password,
	})
	return data
}

/** Вход сотрудника из Telegram Mini App по подписи initData, без пароля. */
export const authenticateTelegram = async (initData: string): Promise<AuthResponse> => {
	const { data } = await authClient.post<AuthResponse>(AUTH_ENDPOINTS.TELEGRAM_LOGIN, { initData })
	return data
}

/** Вход по одноразовому коду, который бот навигации присылает сотруднику. */
export const authenticateCode = async (code: string): Promise<AuthResponse> => {
	const { data } = await authClient.post<AuthResponse>(AUTH_ENDPOINTS.CODE_LOGIN, { code })
	return data
}

/** Поля, которые виджет Telegram передаёт в колбэк после входа. */
export interface TelegramWidgetUser {
	id: number
	first_name: string
	last_name?: string
	username?: string
	photo_url?: string
	auth_date: number
	hash: string
}

/** Вход через виджет Telegram на сайте: подпись проверяет api. */
export const authenticateTelegramWidget = async (
	widget: TelegramWidgetUser,
): Promise<AuthResponse> => {
	const { data } = await authClient.post<AuthResponse>(AUTH_ENDPOINTS.TELEGRAM_WIDGET_LOGIN, widget)
	return data
}

/** Ссылка на бота, по которой Telegram привязывается к текущей учётной записи. */
export const requestTelegramLink = async (): Promise<{ url: string; code: string }> => {
	const { data } = await authClient.post<{ url: string; code: string }>(AUTH_ENDPOINTS.TELEGRAM_LINK)
	return data
}

/** Fetches the currently authenticated user's profile. */
export const getCurrentUser = async (): Promise<User> => {
	const { data } = await authClient.get<User>(AUTH_ENDPOINTS.ME)
	return data
}

/** Ends the session on the backend, dropping the refresh cookie. */
export const logoutRequest = async (): Promise<void> => {
	await authClient.post(AUTH_ENDPOINTS.LOGOUT)
}

/** React Query mutation that logs in by email and persists the session to sessionStorage. */
export const useEmailAuth = () => {
	return useMutation({
		mutationFn: authenticateEmail,
		onSuccess: (data) => {
			sessionStorage.setItem(AUTH_STORAGE_KEYS.ACCESS_TOKEN, data.accessToken)
			if (data.user) {
				sessionStorage.setItem(AUTH_STORAGE_KEYS.USER, JSON.stringify(data.user))
			}
		},
	})
}
